import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import Portal from 'src/shared/components/utils/portals/portal'
import ConsumerAuthDialog from 'src/shared/features/auth/components/consumer/auth-dialog'
import HomeownerImg from 'src/shared/features/home/assets/family.jpg'
import ProfessionalImg from 'src/shared/features/home/assets/re-login.png'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

import { DirectionArrow } from '../../../assets/direction-arrow'

const AuthModalCard = ({
  image,
  title,
  description,
  buttonAction,
}: {
  image: string
  title: string
  description: string
  buttonAction: () => void
}) => {
  return (
    <div className="z-[49] flex w-[300px] flex-col items-start rounded-2xl bg-white p-2.5 shadow-md lg:w-[400px] lg:p-4">
      <img src={image} alt={title} className="h-[150px] w-full rounded-2xl object-cover lg:h-[225px]" />
      <div className="flex flex-row items-end justify-end gap-1 p-1.5 lg:p-3">
        <div className="flex flex-1 flex-col gap-2">
          <div className="text-[20px] font-bold leading-tight text-primary lg:text-[24px]">{title}</div>
          <div className="text-sm text-muted lg:text-base">{description}</div>
        </div>
        <button
          type="button"
          onClick={buttonAction}
          className="flex size-[40px] shrink-0 items-center justify-center rounded-[40px] bg-[#f7f1eb] text-sm text-primary lg:size-[50px] lg:text-base"
        >
          <DirectionArrow className="size-5 lg:size-8" />
        </button>
      </div>
    </div>
  )
}

const AuthModal = ({ flow, open, onClose }: { flow: 'signup' | 'login'; open: boolean; onClose: () => void }) => {
  const navigate = useNavigate()
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const [initialView, setInitialView] = useState<'login' | 'signup'>('login')
  const isMobile = useIsMobile()

  const onClickConsumerLogin = () => {
    if (!isMobile) {
      setAuthDialogOpen(true)
      setInitialView(flow)
    } else {
      if (flow === 'signup') {
        navigate(paths.consumer.auth.signup.getHref())
      } else {
        navigate(paths.consumer.auth.login.getHref())
      }
    }
  }

  const onClickProfessionalLogin = () => {
    if (flow === 'signup') {
      navigate(paths.auth.signup.getHref())
    } else {
      navigate(paths.auth.login.getHref())
    }
  }

  return (
    <Portal>
      <div
        className={cn(
          'fixed inset-0 bottom-0 left-0 right-0 top-0 flex flex-col items-center justify-center gap-4 bg-black/50 transition-opacity duration-300 lg:flex-row lg:gap-8',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <AuthModalCard
          image={HomeownerImg}
          title="I'm an aspiring or current homeowner"
          description="(or looking to rent)"
          buttonAction={onClickConsumerLogin}
        />
        <AuthModalCard
          image={ProfessionalImg}
          title="I'm a real estate professional"
          description="8 pro categories, infinite connections"
          buttonAction={onClickProfessionalLogin}
        />
        <div className="fixed inset-0 z-[48]" onClick={onClose} />
        <div className="absolute z-[48]">
          <ConsumerAuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} initialView={initialView} />
        </div>
      </div>
    </Portal>
  )
}

export default AuthModal
